'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Eggs: From Chicken Feed to Shell"
      author="MicroPlastics Editorial Team"
      date="May 2, 2026"
      readTime="8 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in eggs from chicken feed and packaging"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Microplastics reach eggs through three pathways: (1) <strong>chicken feed
        and drinking water</strong>, which contaminate the hen and transfer to
        eggs; (2) <strong>free-range soil exposure</strong>, where hens pick up
        plastic particles from contaminated outdoor environments; and (3)
        <strong> egg-carton packaging</strong>, especially clear plastic clamshell
        cartons. Counter-intuitively, free-range and pasture eggs can carry
        <em> more</em> microplastic per egg than caged eggs if the outdoor
        environment is plastic-polluted, but they avoid the cage&apos;s plastic
        contact entirely. Glass-cell egg cartons and reusable cardboard cartons
        are the safest packaging.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Eggs from hens fed plastic-contaminated feed contain microplastic; transfer rates from feed to egg yolk have been confirmed in laboratory studies.</>,
          <>Free-range hens forage in outdoor soils that may contain microplastic from atmospheric deposition, agricultural plastic mulch, or wind-blown waste.</>,
          <>Pasture-raised eggs from clean rural environments are likely the cleanest commercial option; caged eggs avoid soil exposure but introduce other plastic contact.</>,
          <>Clear plastic egg cartons contain BPA and phthalates that can leach into the shell, especially in warm display cases.</>,
          <>Choose cardboard or pulp cartons; avoid plastic cartons regardless of brand quality.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Source 1: feed and water contamination
      </h2>
      <p>
        Commercial chicken feed is typically a grain-and-soy mix that has been
        stored in plastic-lined bags or silos for months. Both the feed itself
        and the drinking water provided to hens carry microplastic. A 2024 study
        from Italy detected microplastic in commercial layer feed and tracked
        transfer through to the egg yolk, with PET and polypropylene being the
        dominant polymers.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Source 2: outdoor soil for free-range and pasture hens
      </h2>
      <p>
        Hens are natural foragers and pick up environmental particles as they
        scratch and peck. In urban backyards, near roads, or on farms using plastic
        mulch films, free-range hens have been shown to accumulate measurable
        microplastic from soil intake. This is part of why free-range is not
        automatically &ldquo;cleaner&rdquo;, it depends on the specific
        environment.
      </p>
      <p>
        Pasture-raised eggs from rural farms with clean soil, grazing on natural
        cover, generally test lower than urban free-range eggs.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Source 3: the carton itself
      </h2>
      <p>
        Egg cartons come in three formats:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Pulp / cardboard</strong>, the safest. Recycled paper, no
          plastic contact with the shell.
        </li>
        <li>
          <strong>Foam (#6 polystyrene)</strong>, should be avoided. PS leaches
          styrene and sheds particles, especially in warm display cases.
        </li>
        <li>
          <strong>Clear plastic clamshell (PET #1)</strong>, better than foam but
          still has direct surface contact with the shell, especially during
          transport temperature changes.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Egg types compared
      </h2>

      <BlogComparisonTable
        caption="Egg sourcing and packaging compared for microplastic exposure"
        columns={[
          { key: 'type', label: 'Egg source / packaging' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'notes', label: 'Notes' },
        ]}
        rows={[
          { type: 'Pasture-raised + cardboard carton (rural farm)', risk: 'Lowest', notes: 'Clean soil + inert packaging' },
          { type: 'Organic free-range + cardboard carton', risk: 'Low', notes: 'Organic feed regulations reduce some feed plastic; outdoor varies' },
          { type: 'Conventional free-range + cardboard', risk: 'Moderate', notes: 'Feed contamination + outdoor variable' },
          { type: 'Caged conventional + cardboard', risk: 'Moderate', notes: 'No outdoor exposure but heavy feed contamination' },
          { type: 'Any eggs in clear plastic (PET) carton', risk: 'Higher', notes: 'Adds packaging contact to whatever source' },
          { type: 'Any eggs in foam (PS #6) carton', risk: 'Highest', notes: 'Avoid, styrene-leaching packaging' },
          { type: 'Backyard chickens with controlled feed', risk: 'Lowest', notes: 'Full input control + immediate consumption' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Does cooking affect microplastic content?
      </h2>
      <p>
        Cooking eggs in a non-stick pan can add particles from the pan&apos;s PTFE
        coating, especially if the pan is scratched or used at high heat. A cast-iron
        or stainless-steel pan eliminates this source. Boiling eggs in unfiltered
        tap water can transfer a small amount of microplastic from the water to
        the cooked egg through the shell&apos;s pores. Use filtered water for
        boiling and a metal pan for frying.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Choose cardboard or pulp egg cartons.</strong> Avoid foam #6
          polystyrene and clear plastic PET cartons.
        </li>
        <li>
          <strong>Buy pasture-raised or organic when budget allows.</strong> The
          best balance of low feed contamination and varied soil exposure.
        </li>
        <li>
          <strong>Local rural farm eggs</strong> often outperform supermarket
          organic, shorter supply chain, known environment.
        </li>
        <li>
          <strong>Cook in cast iron or stainless steel</strong>, not non-stick.
        </li>
        <li>
          <strong>Boil in filtered water</strong>, particles transfer through the
          shell.
        </li>
        <li>
          <strong>Skip liquid egg products in plastic cartons.</strong> The yolk
          and white are in direct prolonged plastic contact.
        </li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/microplastics-in-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in food
        </Link>{' '}
        and{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          reduce microplastics in your kitchen
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do eggs contain microplastics?',
            answer:
              'Yes. Microplastics have been detected in egg yolks across multiple studies. They reach eggs through contaminated chicken feed and drinking water, through soil intake by free-range hens, and through plastic packaging that contacts the shell.',
          },
          {
            question: 'Are free-range eggs lower in microplastics?',
            answer:
              'Not automatically. Free-range hens forage in outdoor soils that may contain microplastic from atmospheric deposition or agricultural plastic. Pasture-raised eggs from rural farms with clean soil are typically the cleanest commercial option.',
          },
          {
            question: 'Are organic eggs better for microplastics?',
            answer:
              'Yes, modestly. Organic feed regulations reduce some plastic-source contamination compared to conventional feed. Organic certification also restricts plastic mulch and some agricultural plastics that contribute to soil microplastic load.',
          },
          {
            question: 'What egg carton is safest?',
            answer:
              'Cardboard or molded pulp cartons are safest, recycled paper with no plastic contact with the shell. Avoid foam polystyrene (#6) cartons (styrene leaching) and clear plastic PET cartons (direct shell contact during transport).',
          },
          {
            question: 'Does cooking eggs increase microplastic exposure?',
            answer:
              'It can if you use a non-stick pan with worn PTFE coating or boil in unfiltered tap water. Cooking in cast iron or stainless steel with filtered water eliminates these additional sources without affecting the eggs themselves.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2016.4501',
          },
          {
            authors: 'Coffin S, Wyer H, Leapman D',
            year: 2020,
            title: 'Addressing the environmental and health impacts of microplastics requires the science policy interface',
            publisher: 'PLOS Biology',
            url: 'https://journals.plos.org/plosbiology/article?id=10.1371/journal.pbio.3000932',
          },
          {
            authors: 'Schwabl P, Köppel S, Königshofer P, et al.',
            year: 2019,
            title: 'Detection of various microplastics in human stool',
            publisher: 'Annals of Internal Medicine',
            url: 'https://www.acpjournals.org/doi/10.7326/M19-0618',
          },
          {
            authors: 'Liu Y, Guo R, Zhang S, et al.',
            year: 2022,
            title: 'Uptake and translocation of nano/microplastics by crops',
            publisher: 'Journal of Hazardous Materials',
            url: 'https://www.sciencedirect.com/science/article/pii/S0304389421022603',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
